import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  Switch,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { homeOwnershipInfoService } from '../../../../services/homeOwnershipInfo';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('HomeOwnershipInput');

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const HomeOwnershipInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const [showStateModal, setShowStateModal] = useState(false);
  const [sameAttorney, setSameAttorney] = useState(false);
  const [hasMortgage, setHasMortgage] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeedLocation, setShowDeedLocation] = useState(false);
  const [showTrustLocation, setShowTrustLocation] = useState(false);
  const [formData, setFormData] = useState({
    // Trust/Attorney Information
    trustAttorneyName: '',
    trustAttorneyPracticeName: '',
    trustAttorneyOfficePhone: '',
    trustAttorneyCellPhone: '',
    trustAttorneyEmail: '',
    trustAttorneyStreet: '',
    trustAttorneyCity: '',
    trustAttorneyState: '',
    trustAttorneyZip: '',
    trustAttorneySpecialInstructions: '',

    // Vault-stored location data
    deedLocation: '',
    trustLocation: '',

    // Mortgage/Lender Information
    lenderName: '',
    lenderAccountNumber: '',
    lenderPhone: '',
    lenderEmail: '',
    lenderWebsite: '',
    lenderHasOnlinePortal: false,
    monthlyPaymentAmount: '',
    monthlyPaymentDate: '',
    terminationDate: '',

    // Vault-stored credentials
    username: '',
    password: '',

    // Common fields
    specialInstructions: '',
  });

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Helper: format date as dd/mm/yyyy while typing
  const formatDate = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 8) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    } else if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    }
    return cleaned;
  };

  const handleCurrencyChange = (text: string) => {
    const cleaned = text.replace(/^\$/, '');
    setFormData({ ...formData, monthlyPaymentAmount: formatCurrency(cleaned) });
  };

  const handlePhoneChange = (field: string, text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleDateChange = (field: string, text: string) => {
    const formatted = formatDate(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleSubmit = async () => {
    try {
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password) && !(await ensureCredentialWriteKey())) return;
      // Save the home ownership data with propertyType set to 'own'
      const dataToSave = {
        ...formData,
        propertyType: 'own'
      };
      const savedHomeOwnership = await homeOwnershipInfoService.createHomeOwnershipInfo(dataToSave);
      // Handle wrapped response format { success, data, message }
      const createdData = savedHomeOwnership?.data || savedHomeOwnership;

      // Navigate to lock/unlock screen with the ID
      (navigation as any).navigate('HomeOwnershipLockUnlock', { homeOwnershipId: createdData.id });
    } catch (error) {
      logger.error('Error saving home ownership info:', error);
      Alert.alert('Error', 'Failed to save home ownership information. Please try again.');
    }
  };

  const handleStateSelect = (state: string) => {
    setFormData({ ...formData, trustAttorneyState: state });
    setShowStateModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Add Home Ownership Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Where is the deed to your home located? 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.deedLocation}
                onChangeText={(text) => setFormData({ ...formData, deedLocation: text })}
                placeholder="Enter deed location"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showDeedLocation}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowDeedLocation(!showDeedLocation)}
              >
                <Image
                  source={showDeedLocation ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Where is your Trust document located? 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.trustLocation}
                onChangeText={(text) => setFormData({ ...formData, trustLocation: text })}
                placeholder="Enter trust document location"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showTrustLocation}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowTrustLocation(!showTrustLocation)}
              >
                <Image
                  source={showTrustLocation ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
          </View>

          <Text style={[styles.label, { marginTop: 8 }]}>Who was the attorney or law firm that helped setup the trust?</Text>
          
          <View style={styles.toggleContainer}>
            <Text style={[styles.label, { marginBottom: 0 }]}>Same as your personal attorney?</Text>
            <Switch
              trackColor={{ false: "#767577", true: "#29BFC0" }}
              thumbColor={sameAttorney ? "#fff" : "#f4f3f4"}
              ios_backgroundColor="#767577"
              onValueChange={() => setSameAttorney(!sameAttorney)}
              value={sameAttorney}
            />
          </View>

          {!sameAttorney && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Full Name</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustAttorneyName}
                  onChangeText={(text) => setFormData({ ...formData, trustAttorneyName: text })}
                  placeholder="Enter Full Name"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Practice Name</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustAttorneyPracticeName}
                  onChangeText={(text) => setFormData({ ...formData, trustAttorneyPracticeName: text })}
                  placeholder="Enter Practice name"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Office Phone</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustAttorneyOfficePhone}
                  onChangeText={(text) => handlePhoneChange('trustAttorneyOfficePhone', text)}
                  placeholder="Enter Office Phone"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="phone-pad"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Cell Phone</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustAttorneyCellPhone}
                  onChangeText={(text) => handlePhoneChange('trustAttorneyCellPhone', text)}
                  placeholder="Enter Cell Phone"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="phone-pad"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Email</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustAttorneyEmail}
                  onChangeText={(text) => setFormData({ ...formData, trustAttorneyEmail: text })}
                  placeholder="Enter Email"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Street Address</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustAttorneyStreet}
                  onChangeText={(text) => setFormData({ ...formData, trustAttorneyStreet: text })}
                  placeholder="Enter Street Address"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>City</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustAttorneyCity}
                  onChangeText={(text) => setFormData({ ...formData, trustAttorneyCity: text })}
                  placeholder="Enter City"
                  placeholderTextColor="#A9A9A9"
                  autoCapitalize="words"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>State</Text>
                <TouchableOpacity
                  style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
                  onPress={() => {
                    logger.debug('Opening state modal');
                    setShowStateModal(true);
                  }}
                >
                  <Text style={[
                    { fontSize: 16, color: '#1C2C4F' },
                    !formData.trustAttorneyState && { color: '#A9A9A9' }
                  ]}>
                    {formData.trustAttorneyState || 'Select State'}
                  </Text>
                  <Image
                    source={require('../../../../assets/icons/down.png')}
                    style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
                  />
                </TouchableOpacity>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Zip Code</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustAttorneyZip}
                  onChangeText={(text) => setFormData({ ...formData, trustAttorneyZip: text })}
                  placeholder="Enter Zip Code"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="phone-pad"
                />
              </View>
            </>
          )}

          <View style={styles.toggleContainer}>
            <Text style={[styles.label, { marginBottom: 0 }]}>Do you have a mortgage on your home?</Text>
            <Switch
              trackColor={{ false: "#767577", true: "#29BFC0" }}
              thumbColor={hasMortgage ? "#fff" : "#f4f3f4"}
              ios_backgroundColor="#767577"
              onValueChange={() => setHasMortgage(!hasMortgage)}
              value={hasMortgage}
            />
          </View>

          {hasMortgage && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Lending Company Name</Text>
                <TextInput
                  style={styles.input}
                  value={formData.lenderName}
                  onChangeText={(text) => setFormData({ ...formData, lenderName: text })}
                  placeholder="Enter Company Name"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Loan Number</Text>
                <TextInput
                  style={styles.input}
                  value={formData.lenderAccountNumber}
                  onChangeText={(text) => setFormData({ ...formData, lenderAccountNumber: text })}
                  placeholder="Enter Loan Number"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Loan Termination Date</Text>
                <TextInput
                  style={styles.input}
                  value={formData.terminationDate}
                  onChangeText={(text) => handleDateChange('terminationDate', text)}
                  placeholder="Select Termination Date"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Monthly Payment Due Date</Text>
                <TextInput
                  style={styles.input}
                  value={formData.monthlyPaymentDate}
                  onChangeText={(text) => handleDateChange('monthlyPaymentDate', text)}
                  placeholder="Select Payment Due Date"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Monthly Payment Amount</Text>
                <TextInput
                  style={styles.input}
                  value={formData.monthlyPaymentAmount ? `$${formData.monthlyPaymentAmount}` : ''}
                  onChangeText={(text) => handleCurrencyChange(text)}
                  placeholder="Enter Payment Amount"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Lender Contact (if applicable)</Text>
                <TextInput
                  style={styles.input}
                  value={formData.lenderName}
                  onChangeText={(text) => setFormData({ ...formData, lenderName: text })}
                  placeholder="Enter Contact"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Lender Website</Text>
                <TextInput
                  style={styles.input}
                  value={formData.lenderWebsite}
                  onChangeText={(text) => setFormData({ ...formData, lenderWebsite: text })}
                  placeholder="Enter website address"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Lender Email</Text>
                <TextInput
                  style={styles.input}
                  value={formData.lenderEmail}
                  onChangeText={(text) => setFormData({ ...formData, lenderEmail: text })}
                  placeholder="Enter Email"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Lender Phone Number</Text>
                <TextInput
                  style={styles.input}
                  value={formData.lenderPhone}
                  onChangeText={(text) => handlePhoneChange('lenderPhone', text)}
                  placeholder="Enter Phone Number"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="phone-pad"
                />
              </View>

              <View style={styles.inputGroup}>
                <TouchableOpacity
                  style={styles.portalToggleContainer}
                  onPress={() => setFormData({ ...formData, lenderHasOnlinePortal: !formData.lenderHasOnlinePortal })}
                >
                  <View style={[styles.toggle, formData.lenderHasOnlinePortal && styles.toggleActive]}>
                    <View style={[styles.toggleThumb, formData.lenderHasOnlinePortal && styles.toggleThumbActive]} />
                  </View>
                  <Text style={styles.toggleLabel}>Does your mortgage provider have an online portal?</Text>
                </TouchableOpacity>
                <Text style={styles.helperText}>
                  If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
                </Text>
              </View>

              {formData.lenderHasOnlinePortal && (
                <>
                  <View style={styles.inputGroup}>
                    <Text style={styles.label}>Portal Username 🔒</Text>
                    <View style={styles.passwordContainer}>
                      <TextInput
                        style={[styles.input, styles.passwordInput]}
                        value={formData.username}
                        onChangeText={(text) => setFormData({ ...formData, username: text })}
                        placeholder="Enter Portal Username"
                        placeholderTextColor="#A9A9A9"
                        secureTextEntry={!showUsername}
                        autoCapitalize="none"
                      />
                      <TouchableOpacity
                        style={styles.eyeButton}
                        onPress={() => setShowUsername(!showUsername)}
                      >
                        <Image
                          source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                          style={styles.eyeIcon}
                        />
                      </TouchableOpacity>
                    </View>
                  </View>

                  <View style={styles.inputGroup}>
                    <Text style={styles.label}>Portal Password 🔒</Text>
                    <View style={styles.passwordContainer}>
                      <TextInput
                        style={[styles.input, styles.passwordInput]}
                        value={formData.password}
                        onChangeText={(text) => setFormData({ ...formData, password: text })}
                        placeholder="Enter Portal Password"
                        placeholderTextColor="#A9A9A9"
                        secureTextEntry={!showPassword}
                      />
                      <TouchableOpacity
                        style={styles.eyeButton}
                        onPress={() => setShowPassword(!showPassword)}
                      >
                        <Image
                          source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                          style={styles.eyeIcon}
                        />
                      </TouchableOpacity>
                    </View>
                  </View>
                </>
              )}

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Notes</Text>
                <TextInput
                  style={styles.input}
                  value={formData.notes}
                  onChangeText={(text) => setFormData({ ...formData, notes: text })}
                  placeholder="Enter Any Notes"
                  placeholderTextColor="#A9A9A9"
                />
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.trustAttorneySpecialInstructions}
              onChangeText={(text) => setFormData({ ...formData, trustAttorneySpecialInstructions: text })}
              placeholder="Enter any Special Instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.submitButton}
              onPress={handleSubmit}
            >
              <Text style={styles.submitButtonText}>Submit</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <Modal
        visible={showStateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStateModal(false)}
        statusBarTranslucent
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select State</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowStateModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {US_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.stateItem}
                  onPress={() => handleStateSelect(state)}
                >
                  <Text style={styles.stateItemText}>{state}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(16),
    paddingHorizontal: ms(4),
  },
  portalToggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    justifyContent: 'center',
    padding: ms(2),
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },

});
